import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Tag, Filter, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOverlayState } from '../context/OverlayContext';
import { AVAILABLE_INTERESTS } from '../constants/interests';
import ToggleChip from './ui/ToggleChip';

export interface FilterState {
  nearbyOnly: boolean;
  maxDistance: number;
  dateFrom: string;
  dateTo: string;
  category: string;
  favoritesOnly: boolean;
}

interface FilterMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterState) => void;
  currentFilters: FilterState;
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  isOpen,
  onClose,
  onApplyFilters,
  currentFilters,
}) => {
  const { currentUser, requestAndStoreCurrentLocation } = useAuth();
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [isEnablingLocation, setIsEnablingLocation] = useState(false);

  useOverlayState(isOpen, 'filter-menu');

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters, isOpen]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const hasLocation = currentUser?.latitude && currentUser?.longitude;

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleEnableLocation = async () => {
    setIsEnablingLocation(true);
    try {
      await requestAndStoreCurrentLocation();
    } catch (error) {
      console.error('Failed to enable location:', error);
    } finally {
      setIsEnablingLocation(false);
    }
  };

  const handleReset = () => {
    const resetFilters: FilterState = {
      nearbyOnly: false,
      maxDistance: 50,
      dateFrom: '',
      dateTo: '',
      category: '',
      favoritesOnly: false,
    };
    setFilters(resetFilters);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-cards rounded-t-2xl sm:rounded-2xl w-full sm:max-w-xl max-h-[85vh] sm:max-h-[90vh] overflow-y-auto shadow-card border-t sm:border border-theme-cards mb-0 sm:mb-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-theme-cards border-b border-theme-background/30 p-3 sm:p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-theme-primary" />
            <h2 className="text-base font-semibold text-white">Find Your Vybe</h2>
          </div>
          <button
            onClick={onClose}
            className="text-theme-text-secondary hover:text-white transition-colors text-xl"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content: 2-col on desktop, 1-col mobile */}
        <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
          {/* Quick toggles */}
          <div className="flex flex-wrap gap-2">
            <ToggleChip
              active={filters.favoritesOnly}
              onToggle={() =>
                setFilters({ ...filters, favoritesOnly: !filters.favoritesOnly })
              }
              icon={<Heart className="w-4 h-4" />}
            >
              Favourites
            </ToggleChip>
            {hasLocation && (
              <ToggleChip
                active={filters.nearbyOnly}
                onToggle={() =>
                  setFilters({ ...filters, nearbyOnly: !filters.nearbyOnly })
                }
                icon={<MapPin className="w-4 h-4" />}
              >
                Nearby
              </ToggleChip>
            )}
          </div>

          {/* Distance Slider (when enabled) */}
          {hasLocation && filters.nearbyOnly && (
            <div className="bg-theme-background/40 border border-theme-cards rounded-xl p-3">
              <label className="block text-xs font-medium text-theme-text-main mb-2">
                {filters.maxDistance >= 500 ? 'National' : `Within ${filters.maxDistance} miles`}
              </label>
              <input
                type="range"
                min="5"
                max="500"
                step="5"
                value={filters.maxDistance}
                onChange={(e) =>
                  setFilters({ ...filters, maxDistance: parseInt(e.target.value) })
                }
                className="w-full h-2 accent-theme-primary"
              />
              <div className="flex justify-between text-xs text-theme-text-secondary mt-1">
                <span>5 mi</span>
                <span>National</span>
              </div>
            </div>
          )}

          {/* Location Enable CTA */}
          {!hasLocation && (
            <div className="bg-theme-background/40 border border-theme-cards rounded-xl p-3">
              <div className="flex items-start gap-2">
                <MapPin size={16} className="text-theme-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-theme-text-main font-medium mb-2">
                    Enable location to filter by distance
                  </p>
                  <button
                    onClick={handleEnableLocation}
                    disabled={isEnablingLocation}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEnablingLocation ? 'Enabling...' : 'Enable Now'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Date range - 2 columns on desktop */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-xs font-medium text-theme-text-main flex items-center gap-1">
                <Calendar className="w-4 h-4 text-theme-primary" /> Date from
              </div>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters({ ...filters, dateFrom: e.target.value })
                }
                className="w-full rounded-xl bg-theme-background/40 border border-theme-cards px-3 py-2 text-sm text-theme-text-main focus:outline-none focus:ring-2 focus:ring-theme-primary/50"
              />
            </div>
            <div className="space-y-1">
              <div className="text-xs font-medium text-theme-text-main">Date to</div>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters({ ...filters, dateTo: e.target.value })
                }
                className="w-full rounded-xl bg-theme-background/40 border border-theme-cards px-3 py-2 text-sm text-theme-text-main focus:outline-none focus:ring-2 focus:ring-theme-primary/50"
              />
            </div>
          </div>

          {/* Category */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-theme-text-main flex items-center gap-1">
              <Tag className="w-4 h-4 text-theme-primary" /> Category
            </div>
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="w-full rounded-xl bg-theme-background/40 border border-theme-cards px-3 py-2 text-sm text-theme-text-main focus:outline-none focus:ring-2 focus:ring-theme-primary/50"
            >
              <option value="">All Categories</option>
              {AVAILABLE_INTERESTS.map((interest) => (
                <option key={interest} value={interest}>
                  {interest}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-theme-cards border-t border-theme-background/30 p-4 flex justify-between items-center pb-[calc(env(safe-area-inset-bottom,0px)+16px)] sm:pb-4">
          <button
            onClick={handleReset}
            className="text-sm font-medium text-theme-text-secondary hover:text-white underline underline-offset-2 transition-colors"
          >
            Reset all
          </button>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-full text-sm font-semibold border border-gray-600 hover:border-gray-500 text-white hover:bg-gray-800/50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2.5 rounded-full text-sm font-semibold bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white shadow-lg transition-all"
            >
              Apply filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterMenu;
